import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock, ShieldCheck, CheckCircle2, ArrowUpRight } from "lucide-react";
import Seo, { SITE, localBusinessLd, breadcrumbLd, faqLd, serviceLd } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { zoneBySlug, zoneUrl, PRESTATIONS } from "@/constants/zones";
import { GUIDES, guideUrl } from "@/constants/guides";

const ETAPES = [
  { n: "01", t: "Décrivez votre projet", d: "En ligne, avec vos propres mots. Estimation en 5 minutes." },
  { n: "02", t: "Visite technique gratuite", d: "Mesures, état des supports, contraintes d'accès." },
  { n: "03", t: "Devis détaillé poste par poste", d: "Aucune ligne forfaitaire opaque, prix tenus." },
  { n: "04", t: "Chantier suivi en ligne", d: "Phases datées, photos, factures, messages." },
];

export default function ZonePage() {
  const { slug } = useParams();
  const zone = zoneBySlug(slug);
  if (!zone) return <Navigate to="/blog/zones-intervention" replace />;

  const path = zoneUrl(zone.slug);
  const label = zone.type === "ville" ? `à ${zone.name}` : zone.type === "region" ? `en ${zone.name}` : `en ${zone.name}`;
  const crumbs = [
    { to: "/", label: "Accueil" },
    { to: "/blog/zones-intervention", label: "Zones d'intervention" },
    { to: path, label: zone.name },
  ];
  const guides = GUIDES.filter((g) => g.zone === zone.slug).slice(0, 2);
  const guidesFallback = guides.length ? guides : GUIDES.slice(0, 2);

  return (
    <div data-testid={`zone-page-${zone.slug}`}>
      <Seo
        title={zone.title}
        description={zone.description}
        path={path}
        image={zone.img}
        jsonLd={[
          localBusinessLd([zone.name, "Île-de-France"]),
          serviceLd({
            name: `Travaux intérieurs et rénovation ${label}`,
            description: zone.description,
            area: zone.name,
            path,
          }),
          breadcrumbLd(crumbs),
          faqLd(zone.faq),
        ]}
      />

      {/* HERO */}
      <section className="relative bg-anthracite overflow-hidden">
        <div className="absolute inset-0">
          <img src={zone.img} alt={`Rénovation intérieure ${label} par RAMA DÉCO`} className="w-full h-full object-cover opacity-45" width="1600" height="1000" />
          <div className="absolute inset-0 bg-gradient-to-b from-anthracite/85 via-anthracite/70 to-anthracite/95" />
        </div>
        <div className="relative wrap pt-[120px] md:pt-[170px] pb-16 md:pb-24">
          <Breadcrumbs items={crumbs} tone="dark" />
          <div className="font-mono-caption text-ivory/70 mt-8 mb-5">{zone.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ivory max-w-4xl">
            {zone.h1.split(" ").slice(0, -1).join(" ")} <span className="italic">{zone.h1.split(" ").slice(-1)}</span>
          </h1>
          <div className="mt-8 max-w-2xl space-y-5">
            {zone.intro.map((p, i) => (
              <p key={i} className="text-ivory/85 text-base md:text-lg leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/estimation" data-testid="zone-cta-estimation" className="btn-primary">
              Estimer mon projet {label} <ArrowRight size={16} />
            </Link>
            <a href={`tel:${SITE.phone}`} data-testid="zone-cta-phone" className="btn-ghost btn-ghost-light">
              <Phone size={16} /> {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* BANDEAU INFOS LOCALES */}
      <section className="bg-ivory border-b border-black/10">
        <div className="wrap grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-black/10">
          {[
            { icon: MapPin, t: "Notre base", d: `${SITE.street}, ${SITE.postalCode} ${SITE.city}` },
            { icon: Clock, t: "Délai de visite", d: zone.delai },
            { icon: ShieldCheck, t: "Devis gratuit", d: "Déplacement et chiffrage sans engagement" },
            { icon: Phone, t: "Nous joindre", d: `${SITE.hours} · ${SITE.phoneDisplay}` },
          ].map((it, i) => (
            <div key={i} className="p-6 md:p-8">
              <it.icon size={18} className="text-bordeaux-700" strokeWidth={1.6} />
              <div className="font-display text-base md:text-lg text-anthracite mt-3">{it.t}</div>
              <div className="text-xs md:text-sm text-ink-soft mt-1 leading-snug">{it.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTEXTE LOCAL */}
      <section className="section">
        <div className="wrap">
          <div className="font-mono-caption text-bordeaux-700 mb-4">Le bâti {label}</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite max-w-3xl leading-[1.05]">
            Ce que nous rencontrons sur nos chantiers <span className="italic text-bordeaux-700">{label}.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-rama-md overflow-hidden">
            {zone.contexte.map((c, i) => (
              <div key={i} data-testid={`zone-contexte-${i}`} className="bg-white p-7 md:p-9">
                <div className="font-mono-caption text-ink-soft mb-4">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-xl md:text-2xl text-anthracite leading-tight">{c.t}</h3>
                <p className="mt-3 text-sm md:text-base text-ink-soft leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {zone.typologies.map((t) => (
              <span key={t} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/15 text-xs md:text-sm text-anthracite bg-white">
                <CheckCircle2 size={14} className="text-bordeaux-700" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="section bg-greige/30">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="font-mono-caption text-ink-soft mb-3">Nos prestations {label}</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite">
                Tous les métiers intérieurs,<br /><span className="italic text-bordeaux-700">un seul interlocuteur.</span>
              </h2>
            </div>
            <Link to="/nos-metiers" data-testid="zone-link-metiers" className="btn-ghost self-start">Détail des métiers <ArrowRight size={16} /></Link>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRESTATIONS.map((p) => (
              <li key={p} className="flex items-start gap-3 bg-white border border-black/10 rounded-rama-sm p-4 text-sm md:text-base text-anthracite">
                <CheckCircle2 size={18} className="text-bordeaux-700 shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-ink-soft max-w-2xl">
            RAMA DÉCO n'intervient pas sur le gros œuvre : ni fondations, ni murs porteurs, ni charpente, ni structure.
          </p>
        </div>
      </section>

      {/* NOTRE APPROCHE DU PRIX */}
      <section className="section">
        <div className="wrap grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono-caption text-bordeaux-700 mb-4">Notre approche du prix</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite leading-[1.05]">
              Un prix juste,<br /><span className="italic">calculé pour votre chantier.</span>
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              Nous n'affichons pas de tarif au mètre carré : deux logements de même surface {label} ne demandent
              jamais le même travail. Le prix est établi après la visite technique, à partir des surfaces réelles,
              de l'état des supports et des contraintes d'accès.
            </p>
            {zone.prixNote && (
              <p className="mt-5 p-5 bg-bordeaux-light border-l-2 border-bordeaux-700 text-sm text-anthracite leading-relaxed rounded-r-rama-sm">
                {zone.prixNote}
              </p>
            )}
            <Link to="/estimation" data-testid="zone-cta-estimation-prix" className="mt-8 btn-primary">
              Obtenir mon estimation <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-black/10 rounded-rama-md overflow-hidden bg-white">
              {[
                { t: "Devis détaillé poste par poste", d: "Chaque prestation, chaque surface, chaque produit : aucune ligne forfaitaire opaque, aucune zone d'ombre." },
                { t: "Préparation chiffrée séparément", d: "Lessivage, rebouchage, enduits et ponçage sont distingués de l'application. C'est ce qui rend une comparaison honnête possible." },
                { t: "Prix tenus, avenants écrits", d: "Les prix du devis sont maintenus pendant la durée annoncée. Toute découverte après dépose est chiffrée et validée par vous avant exécution." },
                { t: "TVA réduite appliquée directement", d: "10 % sur les travaux d'amélioration d'un logement de plus de deux ans, 5,5 % sur certains travaux énergétiques, sans démarche de votre côté." },
                { t: "Visite et conseils gratuits", d: "Le déplacement, les mesures et le devis sont offerts, sans engagement et sans majoration de zone." },
              ].map((p, i) => (
                <div key={p.t} className={`p-5 md:p-7 ${i ? "border-t border-black/10" : ""}`}>
                  <div className="font-display text-lg md:text-xl text-anthracite">{p.t}</div>
                  <div className="mt-2 text-sm text-ink-soft leading-relaxed">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ETAPES */}
      <section className="section bg-anthracite text-ivory">
        <div className="wrap">
          <div className="font-mono-caption text-greige mb-4">Comment se déroule un chantier {label}</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.05]">
            Quatre étapes, <span className="italic text-greige">zéro surprise.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ETAPES.map((e) => (
              <div key={e.n} className="border-t border-white/20 pt-5">
                <div className="font-mono-caption text-greige mb-3">{e.n}</div>
                <h3 className="font-display text-xl leading-tight">{e.t}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="wrap grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono-caption text-bordeaux-700 mb-4">Questions fréquentes</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-anthracite leading-tight">
              Ce que l'on nous demande {label}.
            </h2>
            <Link to="/contact" data-testid="zone-link-contact" className="mt-6 inline-flex items-center gap-2 text-anthracite font-semibold link-underline">
              Poser une autre question <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-8 divide-y divide-black/10 border-y border-black/10">
            {zone.faq.map((f, i) => (
              <details key={i} data-testid={`zone-faq-${i}`} className="group py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-lg md:text-xl text-anthracite leading-snug">{f.q}</h3>
                  <span className="text-bordeaux-700 shrink-0 mt-1 transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm md:text-base text-ink-soft leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDES + VOISINS */}
      <section className="section bg-greige/30">
        <div className="wrap grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="font-mono-caption text-ink-soft mb-4">À lire avant de vous lancer</div>
            <div className="space-y-4">
              {guidesFallback.map((g) => (
                <Link key={g.slug} to={guideUrl(g.slug)} data-testid={`zone-guide-${g.slug}`} className="block bg-white border border-black/10 rounded-rama-md p-6 hover:border-bordeaux-700 transition-colors">
                  <div className="font-mono-caption text-bordeaux-700 mb-2">{g.kicker} · {g.readTime}</div>
                  <h3 className="font-display text-xl md:text-2xl text-anthracite leading-tight">{g.h1}</h3>
                  <p className="mt-2 text-sm text-ink-soft line-clamp-2">{g.chapo}</p>
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="font-mono-caption text-ink-soft mb-4">Nous intervenons aussi</div>
            <ul className="flex flex-wrap gap-2">
              {zone.voisins.map((v) => {
                const z = zoneBySlug(v);
                if (!z) return null;
                return (
                  <li key={v}>
                    <Link to={zoneUrl(v)} data-testid={`zone-voisin-${v}`} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-black/15 rounded-full text-sm text-anthracite hover:border-bordeaux-700 hover:text-bordeaux-700 transition-colors">
                      <MapPin size={13} /> {z.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link to="/blog/zones-intervention" data-testid="zone-link-hub" className="mt-6 inline-flex items-center gap-2 text-anthracite font-semibold link-underline">
              Toutes nos zones d'intervention <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="section">
        <div className="wrap bg-anthracite text-ivory rounded-rama-lg p-8 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="font-mono-caption text-greige mb-4">Votre projet {label}</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
              Une estimation maintenant, une visite <span className="italic text-greige">cette semaine.</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-lg">
              {zone.delai}. Déplacement, chiffrage et conseils gratuits, sans engagement.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/estimation" data-testid="zone-cta-final" className="btn-primary justify-center">Estimer mon projet <ArrowRight size={16} /></Link>
            <a href={`tel:${SITE.phone}`} className="btn-ghost btn-ghost-light justify-center">Appeler {SITE.phoneDisplay}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
